import React from 'react';
import { View, Text, Pressable, StyleSheet, ScrollView } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import CampPageHostList from '../../../camp_screen/components/hosts/CampPageHostList';
import { useEffect, useState, useContext } from 'react';
import DataOps from '../../../../data/DataOps';
import AddCoachModal from '../../components/AddCoachModal';
import { GlobalStateContext } from '../../../../components/general/GlobalStateProvider';
import NotificationOps from '../../../../data/NotificationOps';
import { doc, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';

interface HostCoachesSectionProps {
  isEditing: boolean;
  user: any;
  coaches: string[];
  hostMainCamp: any | null;
  setHostMainCamp: (camp: any | null) => void;
  getMembersSectionTitle: () => string;
  getMembersSubtitle: () => string;
  getEmptyStateText: () => { icon: any; title: string; text: string };
  onAddMyselfAsCoach?: () => void; // will be re-implemented locally
  onOpenAddCoachModal?: () => void; // unused after refactor
}

const HostCoachesSection: React.FC<HostCoachesSectionProps> = ({
  isEditing,
  user,
  coaches,
  hostMainCamp,
  setHostMainCamp,
  getMembersSectionTitle,
  getMembersSubtitle,
  getEmptyStateText,
}) => {
  const { setUser } = useContext(GlobalStateContext);
  const [showAddCoachModal, setShowAddCoachModal] = useState(false);

  useEffect(() => {
    if (!user || user.accountType !== 'host') return;
    const primaryCampId = (user as any).camps && (user as any).camps[0];
    if (primaryCampId) {
      (async () => {
        const campData = await DataOps.fetchDataFromFirebase('camp', primaryCampId);
        if (campData) setHostMainCamp({ ...campData, campId: primaryCampId });
      })();
    } else {
      setHostMainCamp(null);
    }
  }, [user?.userId]);

  const handleAddCoach = async (coach: any) => {
    if (!user || user.accountType !== 'host') return;
    const success = await DataOps.linkCoachToHost(user.userId, coach.userId);
    if (success) {
      const newCoaches = [...coaches, coach.userId];
      const updatedUser = { ...user, coaches: newCoaches };
      setUser(updatedUser);
      if (user.camps && user.camps.length > 0) {
        for (const campId of user.camps) {
          await DataOps.linkCoachToCamp(campId, coach.userId);
        }
      }
      try {
        const campName = user.camps?.[0] ? (await DataOps.fetchDataFromFirebase('camp', user.camps[0]))?.campName : 'a camp';
        await NotificationOps.createNotificationForAnyAccount(
          coach.userId,
          `You have been added as a coach by ${user.firstName} ${user.lastName}${campName !== 'a camp' ? ` to ${campName}` : ''}.`,
          'coach_added',
          undefined,
          user.camps?.[0],
          { campName }
        );
      } catch { }
      toast.success(`Coach ${coach.firstName} ${coach.lastName} added successfully!`, { position: 'bottom-center', autoClose: 3000 });
    } else {
      toast.error('Failed to add coach. Please try again.', { position: 'bottom-center', autoClose: 3000 });
    }
  };

  const handleAddMyselfAsCoach = async () => {
    if (!user || user.accountType !== 'host') return;
    try {
      const coachData = {
        userId: user.userId,
        email: user.email,
        phone: user.phone,
        firstName: user.firstName,
        lastName: user.lastName,
        userName: user.userName,
        profileImage: user.profileImage,
        address: user.address,
        emailVerified: user.emailVerified,
        phoneVerified: user.phoneVerified,
        primaryContactMethod: user.primaryContactMethod,
        accountType: 'coach' as const,
        dateJoined: user.dateJoined,
        hostIds: [user.userId],
        assignedCamps: user.camps || [],
        assignedSessions: [],
        specialties: [],
        certifications: [],
        yearsOfExperience: 0,
        bio: '',
        likedCamps: user.likedCamps || [],
        likedSessions: user.likedSessions || [],
        paymentMethods: user.paymentMethods || [],
        work: user.work,
        favoriteSong: user.favoriteSong,
        birthDecade: user.birthDecade,
        uselessSkill: user.uselessSkill,
        timeSpent: user.timeSpent,
        biographyTitle: user.biographyTitle,
        location: user.location,
        dreamDestination: user.dreamDestination,
        pets: user.pets,
        school: user.school,
        funFact: user.funFact,
        languages: user.languages,
        obsession: user.obsession,
      };
      const coachRef = doc(DataOps.db, 'coach', user.userId);
      await setDoc(coachRef, coachData);
      const success = await DataOps.linkCoachToHost(user.userId, user.userId);
      if (success) {
        const newCoaches = [...coaches, user.userId];
        const updatedUser = { ...user, coaches: newCoaches };
        setUser(updatedUser);
        if (user.camps && user.camps.length > 0) {
          for (const campId of user.camps) {
            await DataOps.linkCoachToCamp(campId, user.userId);
          }
        }
        toast.success('You have been added as a coach successfully!', { position: 'bottom-center', autoClose: 3000 });
      } else {
        toast.error('Failed to add yourself as a coach. Please try again.', { position: 'bottom-center', autoClose: 3000 });
      }
    } catch (error) {
      toast.error('Failed to create coach profile. Please try again.', { position: 'bottom-center', autoClose: 3000 });
    }
  };
  return (
    <>
      <View style={localStyles.dividerContainer}>
        <View style={localStyles.dividerLine}>
          <View style={localStyles.dividerGlow} />
        </View>
      </View>
      <Text style={localStyles.sectionTitle}>{getMembersSectionTitle()}</Text>
      <View style={localStyles.childrenHeader}>
        <Text style={localStyles.childrenSubtitle}>{getMembersSubtitle()}</Text>
        {isEditing && (
          <View style={localStyles.addButtonContainer}>
            {!coaches.includes(user.userId) && (
              <Pressable style={localStyles.addButton} onPress={handleAddMyselfAsCoach}>
                <MaterialCommunityIcons name="account-plus" size={20} color="#4CAF50" />
                <Text style={localStyles.addButtonText}>Add myself as a coach</Text>
              </Pressable>
            )}
            <Pressable style={localStyles.addButton} onPress={() => setShowAddCoachModal(true)}>
              <MaterialCommunityIcons name="plus" size={20} color="#4CAF50" />
              <Text style={localStyles.addButtonText}>Add Coach</Text>
            </Pressable>
          </View>
        )}
      </View>

      {hostMainCamp ? (
        <CampPageHostList camp={hostMainCamp} setCamp={setHostMainCamp} isEditing={isEditing} isPeronalInfoPage={true} hideHeader />
      ) : (
        <View style={localStyles.emptyState}>
          <MaterialCommunityIcons name={getEmptyStateText().icon} size={60} color="#ccc" />
          <Text style={localStyles.emptyTitle}>{getEmptyStateText().title}</Text>
          <Text style={localStyles.emptyText}>{getEmptyStateText().text}</Text>
        </View>
      )}

      {showAddCoachModal && (
        <AddCoachModal
          visible={showAddCoachModal}
          onClose={() => setShowAddCoachModal(false)}
          onAddCoach={handleAddCoach}
        />
      )}
    </>
  );
};

export default HostCoachesSection;

const localStyles = StyleSheet.create({
  dividerContainer: {
    alignItems: 'center',
    marginVertical: 48,
    paddingHorizontal: 40,
  },
  dividerLine: {
    width: '100%',
    height: 1,
    backgroundColor: '#F1F5F9',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dividerGlow: {
    width: 60,
    height: 4,
    backgroundColor: '#3C8248',
    borderRadius: 2,
    position: 'absolute',
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: '#1E293B',
    marginBottom: 8,
    textAlign: 'center',
    letterSpacing: -1,
  },
  childrenHeader: {
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 32,
    gap: 16,
  },
  childrenSubtitle: {
    fontSize: 15,
    color: '#64748B',
    textAlign: 'center',
    maxWidth: 400,
    lineHeight: 22,
  },
  addButtonContainer: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: 8,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: '#3C8248',
    shadowColor: '#3C8248',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  addButtonText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '700',
    color: '#3C8248',
  },
  emptyState: {
    alignItems: 'center',
    padding: 60,
    backgroundColor: '#FFFFFF',
    borderRadius: 32,
    borderWidth: 2,
    borderColor: '#F1F5F9',
    borderStyle: 'dashed',
    marginTop: 8,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: '#1E293B',
    marginTop: 20,
    marginBottom: 8,
    letterSpacing: -0.5,
  },
  emptyText: {
    fontSize: 15,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
    maxWidth: 300,
  },
});


